import { useRef, type MutableRefObject } from "react";

import { useProjectStore } from "@/features/projects/useProjectStore";
import {
  useExtractionStore,
  type ExtractedField,
} from "./useExtractionStore";
import { capNumericConfidenceForNL, mergePrefill } from "./mergePrefill";
import { NUMERIC_FIELD_NAMES, type ProjectDataInput } from "./schema";
import { recognizedDocToPrefill } from "./recognizedToFields";
import type { RecognizedDocument, SituacijaReport } from "@/api/schemas";
import type { IfcDepth } from "./extractionMapper";
import type { ProjectDataFormApi } from "./ProjectDataForm";

/**
 * Off-form prefill writers.
 *
 * On the single-scroll page the sources basket pushed extraction straight
 * into react-hook-form through ``ProjectDataFormApi`` (an in-memory ref).
 * After the Donesi/Provjeri route split the form no longer lives on the
 * same screen as the basket, so that ref is null when an upload lands.
 *
 * These functions write to PERSISTENT state instead of form state:
 *   - scalar field values + IFC-depth payload → ``project.projectData``
 *     (the draft the Provjeri form hydrates from on mount);
 *   - confidence stamps → ``useExtractionStore`` (DXF/IFC stamps are
 *     written by the Dropzone itself; the NL path stamps them here).
 *
 * Pure module functions (no React) so the data path is unit-testable; the
 * ``useDraftPrefillSink`` hook below just adapts them to the
 * ``ProjectDataFormApi`` shape the panels already consume.
 */

/** DXF/IFC: persist scalar values + the opaque IFC-depth payload into the
 * draft so the form hydrates them. The Dropzone already wrote the confidence
 * stamps to the store. */
export function applyExtractionToDraft(
  projectId: string,
  values: Record<string, unknown>,
  ifc: IfcDepth,
) {
  patchDraft(projectId, {
    ...(values as Partial<ProjectDataInput>),
    openings: ifc.openings,
    materials: ifc.materials,
    rooms_with_polygons: ifc.rooms_with_polygons,
    building_footprint_polygon: ifc.building_footprint_polygon,
    extraction_warnings: ifc.extraction_warnings,
  });
}

/** NL: enforce the DXF-wins rule + numeric confidence cap, then persist the
 * surviving values into the draft and the merged stamps into the store. */
export function applyNlExtractionToDraft(
  projectId: string,
  extracted: Record<string, ExtractedField>,
  warnings: string[],
) {
  const capped = capNumericConfidenceForNL(extracted, NUMERIC_FIELD_NAMES);
  const incomingValues: Record<string, unknown> = {};
  for (const [k, payload] of Object.entries(capped)) {
    if (payload.value != null && payload.value !== "") {
      incomingValues[k] = payload.value;
    }
  }
  const store = useExtractionStore.getState();
  const merge = mergePrefill(
    store.confidence,
    store.sourceKind,
    incomingValues,
    capped,
    "nl",
  );
  patchDraft(projectId, merge.valuesToWrite as Partial<ProjectDataInput>);
  useExtractionStore
    .getState()
    .setExtraction(merge.mergedConfidence, warnings, merge.resultSourceKind);
}

/** Persist the zahvat description into the draft. */
export function setZahvatOpisOnDraft(projectId: string, text: string) {
  patchDraft(projectId, { zahvat_opis: text });
}

/**
 * Office profile → draft (Lukin nalaz 14.07.: profil ureda u Postavkama i
 * forma su bile dvije nepovezane evidencije istog podatka). Maps the
 * ``OfficeIdentity`` fields onto their form counterparts, FILL-EMPTY-ONLY:
 * office data is user-typed once in Postavke and valid for every project,
 * but anything already in the draft (typed, extracted from a sudski
 * registar/HKA upload) wins. No confidence stamps — this is the user's own
 * entry, same trust as typing in the form.
 */
const OFFICE_FIELD_MAP: Array<[draftField: string, officeField: string]> = [
  ["ured_name", "name_full"],
  ["ured_address", "ured_addr"],
  ["glavni_projektant_name", "gp_name_full"],
  ["glavni_projektant_license", "hka_broj"],
  ["direktor_name", "direktor_name"],
];

/** The fill-empty patch the office profile would write into a draft — pure
 * (no store access) so ProvjeriPage can also mix it into the form's
 * ``initialValues``: the form reads the draft ONCE at mount, before any
 * effect has had the chance to persist the hydration. */
export function officeProfilePatch(
  draft: Record<string, unknown>,
  office: Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!office) return {};
  const isEmpty = (v: unknown) => v == null || v === "";
  const patch: Record<string, string> = {};
  for (const [draftField, officeField] of OFFICE_FIELD_MAP) {
    const v = office[officeField];
    if (typeof v !== "string" || v.trim() === "") continue;
    if (!isEmpty(draft[draftField])) continue; // fill-empty-only
    patch[draftField] = v.trim();
  }
  return patch;
}

export function applyOfficeProfileToDraft(
  projectId: string,
  office: Record<string, unknown> | null | undefined,
) {
  const store = useProjectStore.getState();
  const current = store.projects.find((p) => p.id === projectId);
  if (!current) return;
  const patch = officeProfilePatch(
    (current.projectData ?? {}) as Record<string, unknown>,
    office,
  );
  if (Object.keys(patch).length === 0) return;
  patchDraft(projectId, patch as Partial<ProjectDataInput>);
}

/**
 * K4 (Donesi konzola) — the inline mini-entry on a Projekt row. A deliberate,
 * explicit user entry: it OVERWRITES prior values (like typing in the form
 * would), but an empty/blank value is dropped so the mini-form can never
 * silently blank out a field the user isn't touching.
 */
export function applyManualFieldsToDraft(
  projectId: string,
  values: Record<string, unknown>,
) {
  const cleaned = Object.fromEntries(
    Object.entries(values).filter(
      ([, v]) => v != null && v !== "" && !(typeof v === "number" && Number.isNaN(v)),
    ),
  );
  if (Object.keys(cleaned).length === 0) return;
  patchDraft(projectId, cleaned as Partial<ProjectDataInput>);
}

/**
 * R4 — after a spatial plan is activated via the upload wizard, point the
 * project at the freshly-live municipality so the next generation cites
 * its plan instead of the "[PLAN NIJE U BAZI]" placeholder (R1's guard
 * then resolves). Unlike the extraction writers this is a deliberate,
 * explicit user action, so it OVERWRITES any prior municipality_code
 * rather than filling-empty-only.
 */
export function setMunicipalityOnDraft(projectId: string, code: string) {
  patchDraft(projectId, { municipality_code: code });
}

/**
 * R2 — recognized-document metadata → draft fields.
 *
 * Precedence (per the locked merge policy): drawing-derived (DXF/IFC)
 * values always win — enforced by the SAME ``mergePrefill`` the NL wave
 * uses; on top of that, a document wave only FILLS EMPTY draft fields —
 * it never overwrites anything already present (typed, NL-extracted or
 * hydrated by an earlier document). Values arrive pre-capped below the
 * stamp threshold, so every hydrated field renders the amber "provjeri"
 * stamp ("iz dokumenta").
 */
export function applyRecognizedDocToDraft(
  projectId: string,
  doc: RecognizedDocument,
) {
  applyPrefillFillEmpty(projectId, recognizedDocToPrefill(doc));
}

/** Fill-empty-only merge of a document-tier prefill into the draft +
 * extraction stamps. Shared by the recognized-document (R2) and situacija
 * hydration waves — one merge policy, two sources. */
function applyPrefillFillEmpty(
  projectId: string,
  prefill: Record<string, ExtractedField>,
) {
  if (Object.keys(prefill).length === 0) return;

  const store = useProjectStore.getState();
  const current = store.projects.find((p) => p.id === projectId);
  const draft = (current?.projectData ?? {}) as Record<string, unknown>;

  const isEmpty = (v: unknown) =>
    v == null || v === "" || (typeof v === "number" && v === 0);

  const incomingConfidence: Record<string, ExtractedField> = {};
  const incomingValues: Record<string, unknown> = {};
  for (const [field, payload] of Object.entries(prefill)) {
    if (!isEmpty(draft[field])) continue; // fill-empty-only
    incomingConfidence[field] = payload;
    incomingValues[field] = payload.value;
  }
  if (Object.keys(incomingValues).length === 0) return;

  const ext = useExtractionStore.getState();
  // Same trust tier as NL: never beats an engineering (DXF/IFC) stamp.
  const merge = mergePrefill(
    ext.confidence,
    ext.sourceKind,
    incomingValues,
    incomingConfidence,
    "nl",
  );
  patchDraft(projectId, merge.valuesToWrite as Partial<ProjectDataInput>);
  useExtractionStore
    .getState()
    .setExtraction(merge.mergedConfidence, [], merge.resultSourceKind);
}

/** Situacija-report figures → draft form fields, same document trust tier
 * (capped at 0.69 → amber "provjeri" stamp) and fill-empty-only. Without
 * this, a perfectly parsed situacija showed its figures only in the Donesi
 * panel while the Provjeri form stayed blank — "ekstrakcija radi,
 * hidratacija ne" (D2-class gap, Lukin nalaz 13.07.). */
const SITUACIJA_FIELD_MAP: Array<[draftField: string, paramKey: string]> = [
  ["cestica_povrsina_m2", "parcela_m2"],
  ["tlocrtna_projekcija_m2", "tp_m2"],
  ["num_parking", "parking_pm"],
  ["zelena_povrsina_m2", "horti_num_m2"],
  ["katnost", "katnost"],
];
const SITUACIJA_CONFIDENCE = 0.69;

export function applySituacijaToDraft(
  projectId: string,
  report: SituacijaReport,
) {
  const params = (report.params ?? {}) as Record<string, unknown>;
  const prefill: Record<string, ExtractedField> = {};
  for (const [field, key] of SITUACIJA_FIELD_MAP) {
    const v = params[key];
    if (v == null || v === "") continue;
    prefill[field] = { value: v, confidence: SITUACIJA_CONFIDENCE };
  }
  applyPrefillFillEmpty(projectId, prefill);
}

/**
 * Returns an object with the SAME shape as ``ProjectDataFormApi`` — so
 * ``SourcesPanel`` / ``OpisZahvataPanel`` consume it unchanged — backed by
 * the persistent writers above instead of form state.
 */
export function useDraftPrefillSink(
  projectId: string,
): MutableRefObject<ProjectDataFormApi | null> {
  // Keep the live projectId in a ref so the (once-built) sink methods always
  // patch the project the user is currently on, even if this hook is reused
  // across a param change without a remount.
  const pidRef = useRef(projectId);
  pidRef.current = projectId;

  const ref = useRef<ProjectDataFormApi | null>(null);
  if (ref.current === null) {
    ref.current = {
      applyExtraction: (values, ifc) =>
        applyExtractionToDraft(pidRef.current, values, ifc),
      applyNlExtraction: (extracted, warnings) =>
        applyNlExtractionToDraft(pidRef.current, extracted, warnings),
      setZahvatOpis: (text) => setZahvatOpisOnDraft(pidRef.current, text),
    };
  }
  return ref;
}

/** Merge a partial patch into ``project.projectData`` and upsert. Reads the
 * store at call time so it never closes over a stale project snapshot. */
function patchDraft(projectId: string, partial: Partial<ProjectDataInput>) {
  const store = useProjectStore.getState();
  const current = store.projects.find((p) => p.id === projectId);
  if (!current) return;
  store.upsert({
    ...current,
    projectData: { ...current.projectData, ...partial },
    updatedAt: new Date().toISOString(),
  });
}
